import React, { useState, useRef, useEffect, useContext } from 'react'
import ChatHeader from './ChatHeader'
import ContentEditable from 'react-contenteditable'
import MessageDisplay from './MessageDisplay'
import PromptMessageDisplay from './PromptMessageDisplay'
import MobileChatHeader from './MobileChatHeader'
import { makeStyles } from '@material-ui/core/styles'
import SendIcon from '@material-ui/icons/Send'
import MoodIcon from '@material-ui/icons/Mood'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import EmojiPicker from './EmojiPicker'
import { SessionContext } from './session-context'
import { sendHaltTypingEvent, sendTypingEvent } from './js/data-providers'

const useStyles = makeStyles(theme => ({
  mobileSendButton: {
    paddingTop: '12px',
    cursor: 'pointer',
    '&:hover svg': {
      color: theme.palette.primary.main
    },
    position: 'absolute',
    right: 16
  },
  mobileSendButtonReady: {
    paddingTop: '12px',
    cursor: 'pointer',
    '&:hover svg': {
      color: theme.palette.primary.main
    }
  },
  mobileInput: {
    borderBottom: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderTop: 'none',
    display: 'block',
    '&:focus': {
      outline: '0px'
    },
    lineHeight: 'normal',
    backgroundColor: 'transparent',
    paddingLeft: 8
  },
  controlButton: {
    color: '#999999',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  controlButtonReady: {
    color: '#287cc2',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  mobileChatBody: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #C5C5C5',
    fontFamily: 'Open Sans',
    overflow: 'scroll'
  },
  mobilePowered: {
    height: 40,
    width: '100%',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999999'
  },
  logo: {
    marginRight: 5
  },
  anchor: {
    marginLeft: 4,
    textDecoration: 'none',
    color: '#999999'
  },
  mobilePoweredInner: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  ieInputBox: {
    width: '100%',
    paddingLeft: '10px',
    paddingRight: '10px',
    height: '2em'
  }
}))

function ChatWindow (props) {
  const { token, chatServiceUrl } = useContext(SessionContext)
  const classes = useStyles()
  const [openEmojiPicker, setOpenEmojiPicker] = useState(false)
  const [contentHTML, setContentHTML] = useState('')
  const inputMessageRef = useRef(null)
  const rangeRef = useRef(null)
  const maxMessageLength = 4000
  const [remainingChars, setRemainingChars] = useState(maxMessageLength)

  const openChatPrompt = props.openChatPrompt
  const chatPromptState = props.chatPromptState
  const sendChat = props.sendChat
  const setDisplayState = props.setDisplayState
  const promptsToClosedPromptEvents = props.promptsToClosedPromptEvents
  const events = props.events.filter(i => i.attributes.kind !== 'join')
  const participants = props.participants
  const visitorParticipantId = participants.find(p => p.attributes.kind === 'visitor')?.id
  const getConversationEvents = props.getConversationEvents
  const activeConversation = props.activeConversation
  const sendChatOrCreateRef = props.sendChatOrCreateRef
  const unconnected = props.unconnected
  const [promptDisabled, setPromptDisabled] = useState(!['end', 'chat', 'option_free'].includes(chatPromptState))
  const [promptPlaceholder, setPromptPlaceholder] = useState('Type your message...')
  const allowMultipleMessages = props.majorChatEvents.agentBeenRouted || props.majorChatEvents.agentHasChatted || props.majorChatEvents.botFlowEnded

  useEffect(() => {
    setPromptDisabled(!['end', 'chat', 'option_free'].includes(chatPromptState))
    if (chatPromptState === 'option') {
      setPromptPlaceholder('Choose an option above...')
    } else if (!promptDisabled) {
      if (events[events.length - 2] && events[events.length - 2].attributes.kind === 'cta_button') {
        setPromptPlaceholder('Click the button link above...')
        setPromptDisabled(true)
      } else {
        setPromptPlaceholder('Type your message...')
      }
    }
  }, [chatPromptState])

  // We don't want to allow the user to send multiple messages in a row unless an Agent is involved in the chat.
  // This code watches the events, and disables the visitor from sending another message if they just sent one,
  // until the bot or agent responds.
  useEffect(() => {
    if (!allowMultipleMessages) {
      if (events.length > 0 && events[events.length - 1].attributes.kind === 'chat' && events[events.length - 1].relationships.from_participant.data.id !== visitorParticipantId) {
        setPromptDisabled(false)
        setPromptPlaceholder('Type your message...')
      } else if (events.length > 0 && ['chat', 'option_prompt', 'option_prompt_free'].includes(events[events.length - 1].attributes.kind) && events[events.length - 1].relationships.from_participant.data.id === visitorParticipantId) {
        setPromptDisabled(true)
        setPromptPlaceholder('Please wait...')
      }
    }
  }, [events])

  // If an agent has joined the chat or the bot flow had ended, we want to allow the visitor to send multiple messages.
  useEffect(() => {
    if (allowMultipleMessages) {
      setPromptDisabled(false)
      setPromptPlaceholder('Type your message...')
    }
  }, [props.majorChatEvents, allowMultipleMessages])
  
  useEffect(() => {
    if (chatPromptState === 'chat' && inputMessageRef.current) {
      inputMessageRef.current.focus()
    }
  }, [inputMessageRef, chatPromptState, promptDisabled])

  const handlePaste = (event) => {
    event.preventDefault()
    const paste = (event.clipboardData || window.clipboardData).getData('text')
    const text = inputMessageRef.current.innerText
    const newText = text + paste
  
    if (newText.length > maxMessageLength) {
      const truncatedText = newText.substring(0, maxMessageLength)
      inputMessageRef.current.innerText = truncatedText
      setRemainingChars(0)
    } else {
      inputMessageRef.current.innerText = newText
      setRemainingChars(maxMessageLength - newText.length)
    }
    setContentHTML(inputMessageRef.current.innerHTML)
    placeCaretAtEnd(inputMessageRef.current)
  }

  const sendInputChat = function () {
    const emoji_imgs = inputMessageRef.current.querySelectorAll('img')

    for (let i = 0; i < emoji_imgs.length; i++) {
      const el = emoji_imgs[i]
      const new_el = document.createTextNode(el.alt)
      el.parentNode.replaceChild(new_el, el)
    }

    const text = inputMessageRef.current.innerText
    if (text.length && text !== '\n') {
      setContentHTML('')
      inputMessageRef.current.innerHTML = ''
      rangeRef.current = null
      sendChatOrCreateRef.current(text)
      inputMessageRef.current.focus()
    }
  }

  const sendInputChatMobile = function (event) {
    event.preventDefault()
    const text = inputMessageRef.current.value
    if (text !== '' && text !== '\n') {
      setContentHTML('')
      inputMessageRef.current.value = ''
      rangeRef.current = null
      sendChatOrCreateRef.current(text)
      inputMessageRef.current.focus()
    }
  }

  function placeCaretAtEnd (el) {
    el.focus()
    if (typeof window.getSelection !== 'undefined' &&
      typeof document.createRange !== 'undefined') {
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
    } else if (typeof document.body.createTextRange !== 'undefined') {
      const textRange = document.body.createTextRange()
      textRange.moveToElementText(el)
      textRange.collapse(false)
      textRange.select()
    }
  }

  function onEmojiSelect (stuff) {
    setOpenEmojiPicker(false)
    const el = createEmojiElement(stuff)
    inputMessageRef.current.appendChild(el)
    inputMessageRef.current.focus()
    inputMessageRef.current.innerHTML = inputMessageRef.current.innerHTML.replace('<br>', '')
    placeCaretAtEnd(inputMessageRef.current)
    setContentHTML(inputMessageRef.current.innerHTML)
  }

  function contentHTMLChanged (event) {
    if (activeConversation) {
      const oldText = contentHTML.replace('<br>', '').trim()
      const newText = event.target.value.replace('<br>', '').trim()
      setRemainingChars(maxMessageLength - newText.length)
      if (oldText === '' && newText === '') {
        return
      }
      if (oldText === '') {
        sendTypingEvent({ conversationID: activeConversation.id, token, chatServiceUrl })
      }
      if (newText === '') {
        sendHaltTypingEvent({ conversationID: activeConversation.id, token, chatServiceUrl })
      }
    }

    const selection = window.getSelection()
    const range = selection.getRangeAt(0)
    rangeRef.current = range

    setContentHTML(event.target.value)
  }

  const keyPressed = function (event) {
    const text = inputMessageRef.current.innerText

    if (text.length >= maxMessageLength && event.key !== 'Backspace' && event.key !== 'Enter') {
      event.preventDefault()
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const emoji_imgs = inputMessageRef.current.querySelectorAll('img')

      for (let i = 0; i < emoji_imgs.length; i++) {
        const el = emoji_imgs[i]
        const new_el = document.createTextNode(el.alt)
        el.parentNode.replaceChild(new_el, el)
      }

      const text = inputMessageRef.current.innerText
      if (text !== '' && text !== '\n') {
        setContentHTML('')
        inputMessageRef.current.innerHTML = ''
        rangeRef.current = null
        sendChatOrCreateRef.current(text)
      }
      setRemainingChars(maxMessageLength)
    }
  }

  const keyPressedPhone = function (event) {
    const text = inputMessageRef.current.innerText

    if (text.length >= maxMessageLength && event.key !== 'Backspace' && event.key !== 'Enter') {
      event.preventDefault()
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const text = inputMessageRef.current.value
      if (text !== '' && text !== '\n') {
        setContentHTML('')
        inputMessageRef.current.value = ''
        rangeRef.current = null
        sendChatOrCreateRef.current(text)
      }
      setRemainingChars(maxMessageLength)
    }
  }

  function createEmojiElement (emoji) {
    const el = document.createElement('span')
    el.innerHTML = emoji.native
    el.setAttribute('style', 'width: 1.2em; height: 1.2em; margin: 0px 0.05em 0px 0.1em; vertical-align: -0.1em;')
    return el
  }

  const loading = false

  let message = ''

  if (events) {
    const chatEvents = events.filter(item => {
      return item.attributes.kind === 'chat'
    })
    if (chatEvents.length) {
      message = chatEvents[0].attributes.body
    }
  }

  if (props.phone && !props.bigPrompt) {
    let inputMode = ''
    if (openChatPrompt && openChatPrompt.attributes.kind === 'email_prompt') {
      inputMode = 'email'
    }

    return (
      <>
        {props.mode === 'landing-page' ? <></> : (
          <MobileChatHeader
            avatarUrl={props.avatarUrl}
            conversationLabel={props.conversationLabel}
            bot={props.bot}
            showConversationHistory={props.showConversationHistory}
            closeChat={props.closeChat}
          />)}
        <div className='chat-box-bottom-mobile' style={{ height: props.mode === 'landing-page' ? '100%' : undefined }}>
          <MessageDisplay
            events={events}
            participants={participants}
            getConversationEvents={getConversationEvents}
            activeConversation={activeConversation}
            loading={loading}
            promptsToClosedPromptEvents={promptsToClosedPromptEvents}
            sendChat={sendChat}
            unconnected={unconnected}
            currentTypingEvent={props.currentTypingEvent}
            phone={props.phone}
            botParticipantID={props.botParticipantID}
            cloudAgentName={props.cloudAgentName}
            cloudAgentAvatar={props.cloudAgentAvatar}
          />
          {!(chatPromptState === 'option' && props.mode.startsWith('modal')) && (
            <div id='controls-mobile'>
              <input
                className={classes.mobileInput}
                type='text'
                placeholder={promptPlaceholder}
                inputMode={inputMode}
                value={contentHTML}
                onChange={(e) => setContentHTML(e.target.value)}
                onKeyDown={keyPressedPhone}
                disabled={promptDisabled}
                onFocus={() => { setDisplayState('open') }}
                ref={inputMessageRef}
              />
              <div
                className={classes.mobileSendButton}
                onClick={sendInputChatMobile}
              ><SendIcon className={contentHTML ? classes.controlButtonReady : classes.controlButton} />
              </div>
            </div>
          )}
        </div>
      </>
    )
  }

  if (props.bigPrompt) {
    return (
      <div onClick={() => setDisplayState('open')} style={{ cursor: 'pointer' }}>
        <ChatHeader
          mode={props.mode}
          avatarUrl={props.avatarUrl}
          conversationLabel={props.conversationLabel}
          bot={props.bot}
          showConversationHistory={props.showConversationHistory}
          bigPrompt={props.bigPrompt}
        />
        <div className='chat-box-bottom-prompt'>
          <PromptMessageDisplay
            message={message.replace(/<p>/g, '').replace(/<\/p>/g, '\n')}
          />
        </div>
      </div>
    )
  }

  const CharacterCounter = (
    !(chatPromptState === 'option' && props.mode.startsWith('modal')) && (
      <>
        <hr style={{ width: '100%', borderTop: '.5pt solid #cccccc', display: remainingChars < 500 ? 'block' : 'none' }} />
        <div style={{ marginLeft: '10px', marginBottom: '10px', color: remainingChars < 100 ? 'red' : 'black' }}>
          {remainingChars < 500 && `${remainingChars} characters remaining`}
        </div>
      </>
    )
  )

  const textEntryComponent = (
    !(chatPromptState === 'option' && props.mode.startsWith('modal')) && (
      <div id='controls'>
        <div
          style={{
            position: 'relative',
            wordWrap: 'break-word',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              zIndex: 1,
              paddingTop: 8,
              paddingLeft: 12,
              paddingBottom: 8,
              color: '#696969',
              position: 'absolute',
              display: (contentHTML) ? 'none' : 'block'
            }}
          >{promptPlaceholder}
          </div>
          <ContentEditable
            style={{
              zIndex: 2,
              background: 'transparent 0 0 repeat scroll',
              width: 'calc(100% - 90px)',
              position: 'relative',
              paddingTop: 8,
              paddingLeft: 12,
              paddingBottom: 8
            }}
            className={classes.mobileInput}
            innerRef={inputMessageRef}
            autoFocus
            html={contentHTML}
            disabled={promptDisabled}
            onChange={contentHTMLChanged} // handle innerHTML change
            onKeyDown={keyPressed}
            onPaste={handlePaste}
            onFocus={() => { setDisplayState('open') }}
          />
        </div>
        <div style={{ 
          paddingTop: '12px', 
          display: promptDisabled ? 'none' : 'block', 
          position: 'absolute', 
          right: 48,
          height: 0,
          overflow: 'visible'
        }}>
          {openEmojiPicker
            ? (
              <>
                <div onMouseDown={(e) => { e.preventDefault(); setOpenEmojiPicker(false) }}>
                  <HighlightOffIcon className={classes.controlButton} />
                </div>
                <ClickAwayListener onClickAway={() => {
                  setOpenEmojiPicker(false)
                }}
                >
                  <div style={{ 
                    position: 'relative',
                    display: 'inline-block',
                    bottom: '155px',
                    right: '80px'
                  }}>
                    <EmojiPicker onSelect={onEmojiSelect} />
                  </div>
                </ClickAwayListener>
              </>
            ) : (
              <div onMouseDown={(e) => { 
                e.preventDefault(); 
                setOpenEmojiPicker(true); 
              }}>
                <MoodIcon className={classes.controlButton} />
              </div>)}
        </div>
        <div className={classes.mobileSendButton} onClick={sendInputChat} style={{ display: promptDisabled ? 'none' : 'block' }}>
          <SendIcon className={contentHTML ? classes.controlButtonReady : classes.controlButton} />
        </div>

      </div>
    )
  )
  const contentHeader = props.mode === 'content-with-header' || props.mode.startsWith('modal')
  let className = props.fullScreen ? 'fullscreen' : null
  if (props.mode.startsWith('modal')) {
    className = props.mode
  }

  return (
    <>
      {props.mode === 'content' || props.mode === 'landing-page' ? <></>
        : (
          <ChatHeader
            avatarUrl={props.avatarUrl}
            conversationLabel={props.conversationLabel}
            showConversationHistory={props.showConversationHistory}
            bigPrompt={props.bigPrompt}
            fullScreen={props.fullScreen}
            closeChat={props.closeChat}
            contentHeader={contentHeader}
            mode={props.mode}
          />)}
      <div id='chat-box-bottom-normal' className={className}>
        <MessageDisplay
          events={events}
          participants={participants}
          getConversationEvents={getConversationEvents}
          activeConversation={activeConversation}
          loading={loading}
          promptsToClosedPromptEvents={promptsToClosedPromptEvents}
          sendChat={sendChat}
          botAvatar={props.avatarUrl}
          unconnected={unconnected}
          currentTypingEvent={props.currentTypingEvent}
          botParticipantID={props.botParticipantID}
          cloudAgentName={props.cloudAgentName}
          cloudAgentAvatar={props.cloudAgentAvatar}
        />
        {textEntryComponent}
        {CharacterCounter}
      </div>
    </>
  )
}

export default ChatWindow
